import { Transform, Type } from 'class-transformer';
import {
	IsBoolean,
	IsDate,
	IsIn,
	IsNotEmpty,
	IsOptional,
	IsString,
	IsUUID,
	IsUrl,
	Length,
	MaxLength,
	ValidateIf,
} from 'class-validator';

const trimString = ({ value }: { value: unknown }) =>
	typeof value === 'string' ? value.trim() : value;

export const COURSES_AMA_STATUSES = ['pending', 'answered', 'archived'] as const;
export type CoursesAmaStatusValue = (typeof COURSES_AMA_STATUSES)[number];

export const COURSES_AMA_STATUS_FILTERS = ['pending', 'answered', 'archived', 'all'] as const;
export type CoursesAmaStatusFilter = (typeof COURSES_AMA_STATUS_FILTERS)[number];

export class CoursesCommunityCourseQueryDto {
	@IsUUID()
	courseId: string;
}

export class CoursesAskMentorDto {
	@IsUUID()
	courseId: string;

	@IsString()
	@Length(10, 1000)
	question: string;
}

export class CoursesSendSupportDto {
	@IsString()
	@Length(3, 200)
	subject: string;

	@IsString()
	@Length(10, 2000)
	message: string;
}

export class CoursesCreateDiscussionDto {
	@IsUUID()
	courseId: string;

	@IsString()
	@Length(5, 200)
	title: string;

	@IsOptional()
	@Transform(trimString)
	@IsString()
	@MaxLength(8000)
	body?: string | null;
}

export class CoursesPostDiscussionReplyDto {
	@Transform(trimString)
	@IsString()
	@Length(1, 4000)
	content: string;
}

export class CoursesSetDiscussionClosedDto {
	@IsBoolean()
	closed: boolean;
}

export class CoursesCreateWebinarDto {
	@IsUUID()
	courseId: string;

	@IsString()
	@Length(1, 200)
	title: string;

	@IsOptional()
	@IsString()
	@MaxLength(4000)
	description?: string | null;

	@Type(() => Date)
	@IsDate()
	proposedAt: Date;
}

export class CoursesUpdateWebinarDto {
	@IsOptional()
	@IsString()
	@Length(1, 200)
	title?: string;

	@IsOptional()
	@IsString()
	@MaxLength(4000)
	description?: string | null;

	@IsOptional()
	@Type(() => Date)
	@IsDate()
	proposedAt?: Date;
}

export class CoursesConfirmWebinarDto {
	@Type(() => Date)
	@IsDate()
	scheduledAt: Date;

	@IsString()
	@IsNotEmpty()
	@IsUrl({ require_tld: false, require_protocol: true, protocols: ['http', 'https'] })
	@MaxLength(1000)
	meetingUrl: string;

	@IsOptional()
	@IsString()
	@MaxLength(4000)
	meetingDetails?: string | null;
}

export class CoursesListAdminAmaQueryDto {
	/** Defaults to `pending` in the service. */
	@IsOptional()
	@IsIn(COURSES_AMA_STATUS_FILTERS)
	status?: CoursesAmaStatusFilter;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	q?: string;

	/** `all` (default) = every course, `unassigned` = rows without course_id, otherwise a course uuid. */
	@ValidateIf(
		(o: CoursesListAdminAmaQueryDto) =>
			o.courseFilter !== undefined && o.courseFilter !== 'all' && o.courseFilter !== 'unassigned'
	)
	@IsUUID()
	courseFilter?: string;
}

export class CoursesAnswerAmaDto {
	@Transform(trimString)
	@IsString()
	@Length(1, 5000)
	answer: string;
}

export class CoursesSetAmaStatusDto {
	@IsIn(COURSES_AMA_STATUSES)
	status: CoursesAmaStatusValue;
}
